import React, { useEffect } from 'react';
import ArcNav from '../components/landing/arc/ArcNav';
import ArcFooter from '../components/landing/arc/ArcFooter';
import '../components/landing/arc/arc.css';

const SHIELDS = [
  { eyebrow: 'Encryption', main: 'AES-256', sub: 'At rest · in transit' },
  { eyebrow: 'Residency', main: 'US Hosted', sub: 'U.S. region' },
  { eyebrow: 'Your Data', main: 'Zero Training', sub: 'Never trains models' },
  { eyebrow: 'Access', main: 'RBAC', sub: 'Least privilege' },
];

const DETAILS = [
  {
    title: 'Encryption',
    body: 'AES-256 at rest across every database and recording store. TLS-encrypted in transit on every hop.',
  },
  {
    title: 'Data residency',
    body: 'All production infrastructure and call data hosted in the United States.',
  },
  {
    title: 'Access controls',
    body: 'Role-based access from agency to workspace to sub-account. Least privilege on every production system.',
  },
  {
    title: 'Conversation records',
    body: 'Every call and chat recorded, transcribed and auditable — with instant human transfer on demand.',
  },
];

const OWNERSHIP = [
  { title: 'You own it', body: 'Every recording, transcript and contact belongs to you and your clients — never to us.' },
  { title: 'Export or delete anytime', body: 'Full data export on request. Deletion honored across recordings, transcripts and contact data.' },
  { title: 'Never used for training', body: 'Your conversations are never used to train AI models — ours or anyone else\u2019s.' },
];

const SUBPROCESSORS = [
  { vendor: 'Twilio', purpose: 'Telephony & SMS carrier', notes: 'Call audio and message routing' },
  { vendor: 'ElevenLabs', purpose: 'Voice synthesis & conversation engine', notes: 'Real-time voice processing' },
  { vendor: 'OpenAI & Anthropic', purpose: 'Language models', notes: 'API traffic excluded from model training' },
  { vendor: 'MongoDB Atlas', purpose: 'Database', notes: 'Encrypted at rest, US region' },
  { vendor: 'Stripe', purpose: 'Payments', notes: 'Card data never touches Voxinity servers' },
  { vendor: 'Google', purpose: 'Business reviews import', notes: 'Public business data only' },
];

const Shield = ({ eyebrow, main, sub }) => (
  <div className="flex flex-col items-center py-10 px-6">
    <div
      className="w-32 h-40 bg-[#D9CFB6] flex flex-col items-center justify-center text-center px-3 pb-10"
      style={{ clipPath: 'polygon(0 0, 100% 0, 100% 68%, 50% 100%, 0 68%)' }}
    >
      <span className="arc-body text-[9px] font-bold uppercase tracking-[0.22em] text-[#141414]/60">{eyebrow}</span>
      <span className="block w-8 h-[1.5px] bg-[#141414]/30 my-2" />
      <span className="arc-head font-black text-lg text-[#141414] leading-tight whitespace-nowrap">{main}</span>
      <span className="arc-body text-[9px] font-bold uppercase tracking-[0.14em] text-[#141414]/55 mt-1.5 leading-snug">{sub}</span>
    </div>
  </div>
);

const TrustSecurityPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen arc-bone relative" data-testid="trust-security-page">
      <ArcNav />
      <main className="pt-32 pb-24">
        <div className="max-w-[1360px] mx-auto px-6">
          {/* Header */}
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-end mb-14">
            <h1 className="arc-head font-black text-[#141414] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Security that
              <br />
              <span className="arc-serif font-medium">passes procurement.</span>
            </h1>
            <p className="arc-body text-base md:text-lg leading-relaxed text-[#141414]/60 lg:pb-2">
              Encryption, access controls and data ownership the way enterprise buyers expect them —
              documented and auditable. Your data stays yours. It never trains a model.
            </p>
          </div>

          {/* Shield grid */}
          <div className="rounded-3xl border border-black/[0.08] bg-white/55 backdrop-blur-xl overflow-hidden mb-4" data-testid="trust-shield-grid">
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-black/[0.06]">
              {SHIELDS.map((s) => <Shield key={s.main} {...s} />)}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-black/[0.06] border-t border-black/[0.06]">
              {DETAILS.map((d) => (
                <div key={d.title} className="px-7 py-6">
                  <p className="arc-body text-[11px] font-bold uppercase tracking-[0.22em] text-[#141414]/45 mb-2">{d.title}</p>
                  <p className="arc-body text-sm leading-relaxed text-[#141414]/70">{d.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Roadmap strip */}
          <div className="rounded-2xl border border-dashed border-black/[0.14] px-7 py-5 mb-16 flex items-center gap-4 flex-wrap" data-testid="trust-roadmap">
            <span className="arc-body text-[10px] font-bold uppercase tracking-[0.25em] text-[#141414]/45 border border-black/[0.12] rounded-full px-3 py-1">Roadmap</span>
            <p className="arc-body text-sm text-[#141414]/60">
              SOC 2 Type II readiness and HIPAA BAA coverage for healthcare deployments are in motion.
              <a href="mailto:security@voxinity.ai" className="font-bold text-[#141414] hover:opacity-70 ml-1">Request our latest security documentation →</a>
            </p>
          </div>

          {/* Your data stays yours */}
          <div className="mb-16" data-testid="trust-ownership">
            <h2 className="arc-head font-black text-[#141414] text-3xl sm:text-4xl tracking-tight mb-8">
              Your data <span className="arc-serif font-medium">stays yours.</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {OWNERSHIP.map((o) => (
                <div key={o.title} className="rounded-2xl border border-black/[0.07] bg-white/55 p-7">
                  <p className="arc-head font-black text-lg text-[#141414] mb-2">{o.title}</p>
                  <p className="arc-body text-sm leading-relaxed text-[#141414]/60">{o.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* PCI by design */}
          <div className="rounded-2xl bg-[#141414] px-8 py-7 mb-16" data-testid="trust-pci">
            <p className="arc-body text-[11px] font-bold uppercase tracking-[0.25em] text-[#F0EFEC]/50 mb-2">Payment safety by design</p>
            <p className="arc-body text-base leading-relaxed text-[#F0EFEC]/80 max-w-3xl">
              Voxinity agents never collect card numbers on a call or in a chat. All payments run on
              Stripe-hosted checkout — card data never touches Voxinity infrastructure.
            </p>
          </div>

          {/* Subprocessors */}
          <div className="mb-16" data-testid="trust-subprocessors">
            <h2 className="arc-head font-black text-[#141414] text-3xl sm:text-4xl tracking-tight mb-3">
              Every vendor in the call path, <span className="arc-serif font-medium">disclosed.</span>
            </h2>
            <p className="arc-body text-sm text-[#141414]/55 mb-8 max-w-2xl">
              No hidden processors. This is the complete list of services that touch your data, and why.
            </p>
            <div className="rounded-2xl border border-black/[0.08] bg-white/55 overflow-hidden overflow-x-auto">
              <table className="w-full text-left min-w-[560px]">
                <thead>
                  <tr className="border-b border-black/[0.06]">
                    <th className="arc-body text-[10px] font-bold uppercase tracking-[0.22em] text-[#141414]/45 px-7 py-4">Vendor</th>
                    <th className="arc-body text-[10px] font-bold uppercase tracking-[0.22em] text-[#141414]/45 px-4 py-4">Purpose</th>
                    <th className="arc-body text-[10px] font-bold uppercase tracking-[0.22em] text-[#141414]/45 px-7 py-4">Data handling</th>
                  </tr>
                </thead>
                <tbody>
                  {SUBPROCESSORS.map((s) => (
                    <tr key={s.vendor} className="border-b border-black/[0.05] last:border-0">
                      <td className="arc-body text-sm font-bold text-[#141414] px-7 py-4 whitespace-nowrap">{s.vendor}</td>
                      <td className="arc-body text-sm text-[#141414]/65 px-4 py-4">{s.purpose}</td>
                      <td className="arc-body text-sm text-[#141414]/55 px-7 py-4">{s.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Incident response + contact */}
          <div className="grid md:grid-cols-2 gap-4" data-testid="trust-contact">
            <div className="rounded-2xl border border-black/[0.07] bg-white/55 p-8">
              <p className="arc-head font-black text-lg text-[#141414] mb-2">Incident response</p>
              <p className="arc-body text-sm leading-relaxed text-[#141414]/60">
                Monitored infrastructure with documented escalation. Affected customers are notified
                directly — no burying incidents in a status page.
              </p>
            </div>
            <div className="rounded-2xl border border-black/[0.07] bg-white/55 p-8">
              <p className="arc-head font-black text-lg text-[#141414] mb-2">Security questions?</p>
              <p className="arc-body text-sm leading-relaxed text-[#141414]/60 mb-4">
                Procurement questionnaires, DPAs, subprocessor details — we answer them fast.
              </p>
              <a
                href="mailto:security@voxinity.ai"
                className="arc-body inline-flex items-center gap-2 rounded-full bg-[#141414] text-[#F0EFEC] text-sm font-bold px-6 py-3 hover:bg-black transition-colors"
                data-testid="trust-contact-btn"
              >
                security@voxinity.ai
              </a>
            </div>
          </div>
        </div>
      </main>
      <ArcFooter />
    </div>
  );
};

export default TrustSecurityPage;
